import { Controller } from "@hotwired/stimulus"

// Security parity with the gem's KNOWN_COLOUR_TOKENS allowlist: never write
// a `data-colour-token` sourced from server/seed data (threshold
// `colour_token`, or a template's damage-levels map) unless it is one of
// these four (CONEK-7586 review LOW-1).
const KNOWN_COLOUR_TOKENS = new Set([
  "ins_no_maintenance_required",
  "ins_green_level",
  "ins_amber_risk",
  "ins_red_risk",
])

/**
 * Inspections severity engine (CONEK-4653, spec §5.4 / §6.3).
 *
 * Client-side MIRROR of the server scoring path
 * (Inspections::Scoring::{ConditionEvaluator,QuestionScorer} + the page-severity
 * rollup in Inspections::Score) so the page-severity pill and the damage diagram
 * update live as the inspector answers — BEFORE the SavePage round-trip. The
 * server stays authoritative: this only drives the optimistic preview.
 *
 * It consumes the SAME compiled structured-condition shape the server uses
 * (`{ all_of: [{ var, op, operand }] }`, operand `{ literal }` or
 * `{ var, factor }`), serialised onto the measurement-slider data attributes
 * by Ui::MeasurementSliderComponent, and the per-option `data-action-required`
 * on each severity radio and on each Additional Observations checkbox. NO
 * eval — only this whitelisted interpreter, matching ConditionEvaluator
 * exactly (absent variable => comparison false; all_of = AND).
 *
 * Markup (rendered across the pager page body) — CONEK-7591: the root sits
 * on the Ui::AdminPageLayoutComponent CONTAINER (html_options), so it wraps
 * the whole step, sticky header zone included (the severityPill target
 * lives inside that zone, above the reference/answers grid):
 *   <div data-controller="inspections--severity-engine"
 *        data-inspections--severity-engine-page-severity-mode-value="normal">
 *     <… sticky header zone … data-…-target="severityPill" …>
 *     <... severityRadio / measurement / observation / diagram targets ...>
 *   </div>
 *
 * CONEK-8802 — on the web swipe pager every page is a slide with its own
 * engine, and the pills of all pages ride the shared sticky header, outside
 * every slide. There the root sits on the slide and names its pill by id
 * (`pill` value) instead of holding it as a target.
 *
 * Also drives each slider's `data-colour-token` (CONEK-7586 A), so a
 * measurement-slider recolours as its own value crosses its thresholds
 * rather than only reflecting the page-max severity — and floors the
 * "Action Required" severity radio(s) from the sliders (CONEK-7586 B): a
 * scoring slider's own band, when amber / red (CONEK-9172), disables any
 * radio option whose action_required falls below that floor.
 *
 * Each `observation` target is one multi_observation question: like the
 * server's QuestionScorer it contributes the max action_required of its
 * checked boxes (0 when none). That contribution raises the page severity
 * AND, as in legacy, floors the radio like a slider band does (CONEK-9022):
 * ticking Corrosion (3) moves Action Required to Urgent and locks the rest
 * until it is unticked.
 */

export default class extends Controller {
  static targets = [
    "severityPill",
    "severityRadio",
    "measurement",
    "observation",
    "diagram",
    "diagramImage",
    "diagramOverlays",
  ]
  static values = {
    severityMode: { type: String, default: "normal" },
    // CONEK-9172 — the lowest page floor that locks Action Required options,
    // decided by the SERVER (Inspections::Scoring::ActionFloor.locking_floor_for:
    // the `inspections.green_level_floor` flag AND a template version whose
    // severity 1 is Green Level). 1 = the CONEK-7586 B / 9022 floor (any
    // reading off its min or severity-1 box locks No maintenance required);
    // 2 = amber-up only. The client never resolves the flag itself.
    lockingFloor: { type: Number, default: 1 },
    // value -> colour token, mirroring InspectionsTemplateVersion#damage_level_for
    // so the client pill colour matches the server outcome exactly (the
    // severity→colour mapping is per-template data, never hardcoded).
    damageLevels: { type: Object, default: {} },
    // value -> translated outcome label (CONEK-8165), so the pill TEXT follows
    // the live severity too. Absent (the /mobile view) => text left as rendered.
    labels: { type: Object, default: {} },
    // Text for a severity missing from `labels` — the server's own fallback
    // in inspection_outcome_label (the neutral action level).
    fallbackLabel: { type: String, default: "" },
    // Id of a pill outside this element, used when there is no severityPill
    // target (CONEK-8802, the web swipe pager's header).
    pill: { type: String, default: "" },
    // CONEK-9164 — text for a page whose required severity radio has nothing
    // checked yet: "not answered" is Pending (grey), never "No maintenance
    // required" (a real, green answer). Empty => the pill keeps the old
    // behaviour (an unchecked group counts as 0).
    pendingLabel: { type: String, default: "" },
  }

  connect() {
    this.recompute()
  }

  recompute(event) {
    this.recordUserChoice(event)
    const variables = this.buildVariableBag()

    // Measurements: score against thresholds (unless thumb-no-effect /
    // disabled), then apply their enabled state and colour token — same as
    // before CONEK-7586 B. `measurementArs` is kept indexed like
    // measurementTargets so computeFloor can reuse each band's ar without
    // re-scoring.
    const measurementArs = this.measurementTargets.map((el) => {
      const { ar, colourToken } = this.scoreMeasurement(el, variables)
      this.applyEnabledState(el, variables)
      this.applyColourToken(el, colourToken)
      return ar
    })

    // CONEK-7586 B: the page floor is the max, across every scoring slider,
    // of its own band ar — and, since CONEK-9022, every ticked
    // observation's ar — then
    // every severity-radio group gets that floor applied before its
    // contribution is read.
    //
    // The SAME page-wide floor is applied to every severityRadioTarget
    // rather than pairing each measurement with its own radio group,
    // because every seeded template page has exactly ONE Action Required
    // radio group — matching the server's per-page max (page_severity_value
    // is a single rollup, not per-question). This is a simplification that
    // holds for the current template corpus, not a structural guarantee: a
    // page with two independent measurement -> radio pairs (each scoring
    // its own Action Required group) would need per-pair floor scoping
    // instead of this page-wide broadcast.
    const observationArs = this.observationTargets.map((group) =>
      this.observationAr(group),
    )
    // CONEK-9172 — a floor below the server-given locking floor locks
    // nothing. At 2 (green_level_floor), a severity-1 reading or box (Green
    // Level: measured, below the action threshold) leaves "No maintenance
    // required" available; the page still scores 1 from the measurement /
    // observation itself. At 1, every positive floor locks (CONEK-7586 B).
    const rawFloor = Math.max(
      this.computeFloor(measurementArs),
      ...observationArs,
    )
    const floor = rawFloor >= this.lockingFloorValue ? rawFloor : 0
    this.severityRadioTargets.forEach((group) => this.applyFloor(group, floor))

    // Read radio contributions AFTER the floor may have moved a locked
    // selection, so page severity reflects the post-move state.
    const perQuestion = [...measurementArs]
    this.severityRadioTargets.forEach((group) => {
      const checked = group.querySelector("input[type=radio]:checked")
      const ar = checked
        ? parseInt(checked.dataset.actionRequired || "0", 10)
        : 0
      perQuestion.push(ar)
    })
    perQuestion.push(...observationArs)

    const pageSeverity = perQuestion.length ? Math.max(...perQuestion) : 0
    if (this.pendingLabelValue && this.hasUnansweredRequiredRadio()) {
      this.showPendingPill()
    } else {
      this.updatePill(pageSeverity)
    }
    this.updateDiagram(pageSeverity)
  }

  // CONEK-7586 B: page floor = max across every scoring slider of
  // max(band ar, "value > min" ? 1 : 0). A thumb-no-effect slider, or one
  // currently disabled by its own enabled_if (checked via `input.disabled`,
  // already set by applyEnabledState this pass), contributes nothing.
  //
  // The "value > min" -> 1 term (CONEK-7586 B.2): a scoring slider touched
  // off its min floors at 1 — which only locks anything while the locking
  // floor is 1 (CONEK-9172, see recompute). The server mirror is
  // Inspections::Scoring::ActionFloor (CONEK-8194), which Inspections::Submit
  // uses to refuse a saved selection below the floor — change both together.
  // A purely cosmetic/context slider must be flagged `thumb_no_effect`
  // (legacy `thumbColour: ins_slider_no_effect`) so it never floors.
  computeFloor(measurementArs) {
    let floor = 0
    this.measurementTargets.forEach((el, index) => {
      if (el.dataset.thumbNoEffect === "true") return
      const input = el.querySelector("input[type=range]")
      if (!input || input.disabled) return

      const minAttr = input.getAttribute("min")
      const parsedMin = minAttr === null ? NaN : parseFloat(minAttr)
      const min = Number.isNaN(parsedMin) ? 0 : parsedMin
      const value = parseFloat(input.value)
      const aboveMin = !Number.isNaN(value) && value > min ? 1 : 0

      const sliderFloor = Math.max(measurementArs[index], aboveMin)
      if (sliderFloor > floor) floor = sliderFloor
    })
    return floor
  }

  // A multi_observation question's contribution: the max action_required of
  // its checked boxes, 0 when none (QuestionScorer's multi_observation rule).
  // CONEK-9022: also its floor under the radio — the server mirror is
  // Inspections::Scoring::ActionFloor, change both together.
  observationAr(group) {
    const checked = group.querySelectorAll("input[type=checkbox]:checked")
    const ars = Array.from(checked, (input) =>
      parseInt(input.dataset.actionRequired || "0", 10),
    )
    return ars.length ? Math.max(...ars) : 0
  }

  // CONEK-8224: a radio the inspector clicked is THEIR choice — remembered on
  // the group (`data-user-choice` = the option's value) so the floor can
  // raise the selection above it and later give it back.
  recordUserChoice(event) {
    const input = event && event.target
    if (!(input instanceof HTMLInputElement) || input.type !== "radio") return
    const group = this.severityRadioTargets.find((g) => g.contains(input))
    if (group && input.checked) group.dataset.userChoice = input.value
  }

  // CONEK-7586 B: lock/unlock a severity-radio group's options against the
  // page floor (native `disabled` + `data-locked`, never a class name).
  // CONEK-8224: the selection is then the inspector's own choice when it
  // clears the floor, else the lowest-ar unlocked option (first in DOM order
  // on a tie) — so a selection the floor raised comes back down with the
  // slider, while one the inspector picked is never lowered. CONEK-8803 —
  // nothing is pre-selected any more, so when nothing is checked and a floor
  // appears, the floor chooses its lowest clearing option (legacy "raised
  // to"); from then on it follows the rules above, so a floor dropping back
  // to 0 lands on the lowest option. It is never cleared again: an empty
  // radio sends nothing, and the server would keep the raised answer. With
  // no floor, nothing is selected for the inspector. Guard: if no option in the group can clear
  // the floor (e.g. the floor outgrew every option in this group), the
  // group ends up with NO locks — clear any stale `disabled`/`data-locked`
  // left by an earlier, lower floor rather than leaving them in place.
  applyFloor(group, floor) {
    const options = Array.from(group.querySelectorAll("input[type=radio]"))
    if (!options.length) return

    const optionAr = (input) =>
      parseInt(input.dataset.actionRequired || "0", 10)

    const clearing = options.filter((input) => optionAr(input) >= floor)
    if (!clearing.length) {
      options.forEach((input) => {
        input.disabled = false
        delete input.dataset.locked
      })
      return
    }

    options.forEach((input) => {
      if (optionAr(input) < floor) {
        input.disabled = true
        input.dataset.locked = "true"
      } else {
        input.disabled = false
        delete input.dataset.locked
      }
    })

    const lowestOf = (inputs) =>
      inputs.reduce((a, b) => (optionAr(a) <= optionAr(b) ? a : b))

    const checked = options.find((input) => input.checked)
    if (!checked) {
      if (floor > 0) {
        // Floor-raised, so its baseline is the group's lowest option.
        group.dataset.userChoice = lowestOf(options).value
        lowestOf(clearing).checked = true
      }
      return
    }

    // First pass (connect): the server-rendered selection counts as the
    // inspector's choice only when it sits ABOVE the option the floor itself
    // forces (lowest clearing option); at or below it, the slider explains
    // it, so the baseline is the group's lowest option. Compared against that
    // option, not the raw floor: a radio whose options skip the floor's value
    // (e.g. a 0/3 radio under a floor of 2) forces the next one up, and a
    // saved one at that level must still count as floor-raised.
    if (!("userChoice" in group.dataset)) {
      group.dataset.userChoice =
        optionAr(checked) > optionAr(lowestOf(clearing))
          ? checked.value
          : lowestOf(options).value
    }

    const userChoice = options.find(
      (input) => input.value === group.dataset.userChoice,
    )
    const wanted =
      userChoice && !userChoice.disabled ? userChoice : lowestOf(clearing)
    if (wanted !== checked) {
      checked.checked = false
      wanted.checked = true
    }
  }

  // value_id -> current numeric value, for published (touched) measurements only.
  buildVariableBag() {
    const bag = {}
    this.measurementTargets.forEach((el) => {
      const valueId = el.dataset.valueId
      if (!valueId) return
      const input = el.querySelector("input[type=range]")
      if (input && input.value !== "") bag[valueId] = parseFloat(input.value)
    })
    return bag
  }

  // Highest TRUE threshold for THIS slider: { ar, colourToken }. Tie on ar
  // keeps the first threshold in array order. ar 0 / colourToken null when
  // no threshold is true, or the slider is thumb-no-effect / disabled.
  scoreMeasurement(el, variables) {
    if (el.dataset.thumbNoEffect === "true") return { ar: 0, colourToken: null }

    const enabledIf = this.parseJSON(el.dataset.enabledIf)
    if (
      enabledIf &&
      Object.keys(enabledIf).length &&
      !this.evaluate(enabledIf, variables)
    ) {
      return { ar: 0, colourToken: null }
    }

    const thresholds = this.parseJSON(el.dataset.thresholds) || []
    let max = 0
    let colourToken = null
    thresholds.forEach((t) => {
      if (this.evaluate(t.condition, variables)) {
        const ar = parseInt(t.action_required, 10) || 0
        if (ar > max) {
          max = ar
          colourToken = t.colour_token || null
        }
      }
    })
    if (max === 0) return { ar: 0, colourToken: null }

    // Only ever write one of the four known tokens: an unknown/unexpected
    // threshold colour_token falls back to the damage-levels-derived token,
    // and if THAT is also unknown, no attribute is written at all.
    const resolved = KNOWN_COLOUR_TOKENS.has(colourToken)
      ? colourToken
      : this.colourTokenFor(max)
    return {
      ar: max,
      colourToken: KNOWN_COLOUR_TOKENS.has(resolved) ? resolved : null,
    }
  }

  // Sets/clears the measurement-slider's own data-colour-token — never a
  // class name (the conek-ui gem owns the Tailwind variants keyed on it).
  applyColourToken(el, token) {
    const input = el.querySelector("input[type=range]")
    if (!input) return
    if (token) {
      input.dataset.colourToken = token
    } else {
      delete input.dataset.colourToken
    }
  }

  applyEnabledState(el, variables) {
    const input = el.querySelector("input[type=range]")
    if (!input) return
    const enabledIf = this.parseJSON(el.dataset.enabledIf)
    const disabled = !!(
      enabledIf &&
      Object.keys(enabledIf).length &&
      !this.evaluate(enabledIf, variables)
    )
    input.disabled = disabled
    const hint = el.querySelector(
      "[data-inspections--measurement-slider-target=hint]",
    )
    if (hint) {
      hint.textContent = disabled ? hint.dataset.disabledHintText || "" : ""
    }
  }

  // Mirror of ConditionEvaluator: all_of = AND; absent var => false.
  evaluate(condition, variables) {
    if (!condition || !Array.isArray(condition.all_of)) return false
    return condition.all_of.every((clause) =>
      this.evaluateClause(clause, variables),
    )
  }

  evaluateClause(clause, variables) {
    const lhs = variables[clause.var]
    if (lhs === undefined || lhs === null) return false
    const rhs = this.resolveOperand(clause.operand, variables)
    if (rhs === null) return false
    switch (clause.op) {
      case ">":
        return lhs > rhs
      case ">=":
        return lhs >= rhs
      default:
        return false
    }
  }

  resolveOperand(operand, variables) {
    if (!operand) return null
    if (Object.prototype.hasOwnProperty.call(operand, "literal"))
      return operand.literal
    if (Object.prototype.hasOwnProperty.call(operand, "var")) {
      const base = variables[operand.var]
      if (base === undefined || base === null) return null
      return base * operand.factor
    }
    return null
  }

  updatePill(severity) {
    const pill = this.pillElement
    if (!pill) return
    const token = this.colourTokenFor(severity)
    pill.dataset.severity = String(severity)
    pill.dataset.colourToken = token
    pill.className = this.pillClasses(token)
    // CONEK-9172 — the TEXT is the Action Required option chosen on the page
    // (InspectionsFlowHelper#inspection_page_pill_label); the colour stays the
    // page's severity. Nothing chosen -> the severity's own label.
    const label =
      this.chosenActionLabel() ??
      this.labelsValue[severity] ??
      this.fallbackLabelValue
    if (label) pill.textContent = label
  }

  chosenActionLabel() {
    for (const group of this.severityRadioTargets) {
      const checked = group.querySelector("input[type=radio]:checked")
      const text = checked?.closest("label")?.textContent?.trim()
      if (text) return text
    }
    return null
  }

  // CONEK-9164 — a required severity radio group with no option checked.
  // The client only judges required severity radios (the only required kind
  // in the templates); the server (Inspections::RequiredAnswers) judges every
  // kind. Keep the two in step if a template ever marks another kind required.
  hasUnansweredRequiredRadio() {
    return this.severityRadioTargets.some(
      (group) =>
        group.querySelector("input[type=radio][required]") &&
        !group.querySelector("input[type=radio]:checked"),
    )
  }

  showPendingPill() {
    const pill = this.pillElement
    if (!pill) return
    delete pill.dataset.severity
    pill.dataset.colourToken = "pending"
    pill.className = this.pillClasses("pending")
    pill.textContent = this.pendingLabelValue
  }

  get pillElement() {
    if (this.hasSeverityPillTarget) return this.severityPillTarget
    return this.pillValue ? document.getElementById(this.pillValue) : null
  }

  updateDiagram(severity) {
    if (!this.hasDiagramTarget || !this.hasDiagramImageTarget) return
    const variants = this.parseJSON(this.diagramTarget.dataset.variants) || []
    if (!variants.length) return

    // Nearest variant at or below severity, else the lowest (server fallback).
    const atOrBelow = variants.filter((v) => Number(v.severity) <= severity)
    const chosen = atOrBelow.length
      ? atOrBelow.reduce((a, b) =>
          Number(a.severity) > Number(b.severity) ? a : b,
        )
      : variants.reduce((a, b) =>
          Number(a.severity) < Number(b.severity) ? a : b,
        )

    if (chosen && chosen.src) this.diagramImageTarget.src = chosen.src
    if (this.hasDiagramOverlaysTarget && chosen) {
      const overlays = chosen.overlays || []
      this.diagramOverlaysTarget.innerHTML = overlays
        .map(
          (label) =>
            `<span class="rounded bg-background/80 px-1.5 py-0.5 text-[11px] font-medium text-foreground shadow-sm">${this.escapeHtml(label)}</span>`,
        )
        .join("")
      this.diagramOverlaysTarget.classList.toggle(
        "hidden",
        overlays.length === 0,
      )
    }
  }

  colourTokenFor(severity) {
    // Prefer the per-template damage-level map (matches the server outcome);
    // fall back to the canonical 4-token ladder if the map is absent.
    const mapped = this.damageLevelsValue && this.damageLevelsValue[severity]
    if (mapped) return mapped
    if (severity >= 3) return "ins_red_risk"
    if (severity === 2) return "ins_amber_risk"
    if (severity === 1) return "ins_green_level"
    return "ins_no_maintenance_required"
  }

  // Must stay identical to InspectionsFlowHelper::PILL_CLASSES and
  // PENDING_PILL_CLASSES. CONEK-9164 — No maintenance required is green (the
  // green level's classes); grey is Pending only.
  pillClasses(token) {
    const map = {
      pending:
        "bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200",
      ins_no_maintenance_required:
        "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
      ins_green_level:
        "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
      ins_amber_risk:
        "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
      ins_red_risk:
        "bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-200",
    }
    return `inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${map[token] || map.ins_no_maintenance_required}`
  }

  parseJSON(raw) {
    if (!raw) return null
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = String(str)
    return div.innerHTML
  }
}
